import { Injectable, Optional } from "@angular/core";

import { StorageKeys, StorageService } from ".";

export interface BetaFilters {
  type: string;
  status: string;
  reviewStatus: string;
  author: string;
  jiraTicketNumber: string;
  sort: string;
}

@Injectable({
  providedIn: "root",
})
export class BetaCommunityService {
  public betaFilterReadableValues: any = {
    bug: "Bug",
    feature_request: "Feature Request",
    post: "Post",
    open: "Open",
    cannot_reproduct: "Cannot Reproduce",
    fixed: "Fixed",
    investigating: "Investigating",
    accepted: "Accepted",
    rejected: "Rejected",
    true: "Only Reviewed",
    false: "Only Not Reviewed",
  };

  public filters: BetaFilters = {
    type: "all",
    status: "all",
    reviewStatus: "all",
    author: "all",
    jiraTicketNumber: "",
    sort: "date",
  };

  public showBetaType: string;
  public showBetaStatus: string;
  public showBetaReviewStatus: any;
  public showBetaAuthor: any;
  public betaSortBy: string;
  public findJiraTicketNumber: any;

  private _betaFilterSettings: any;

  constructor(private storage: StorageService) {}

  buildFilters(queryParams: Record<string, any> = {}) {
    this.filters = {
      type: this.showBetaType || queryParams["type"] || "all",
      status: this.showBetaStatus || queryParams["status"] || "all",
      reviewStatus: this.showBetaReviewStatus || queryParams["reviewStatus"] || "all",
      author: this.showBetaAuthor || queryParams["author"] || "all",
      sort: this.betaSortBy || queryParams["sort"] || "date",
      jiraTicketNumber: this.findJiraTicketNumber || "",
    };
    this.applyFilters(this.filters, false);
    return this.filters;
  }

  applyFilters(filters: BetaFilters, resetJira = true) {
    const nextFilters = { ...filters };
    if (resetJira && nextFilters.jiraTicketNumber) {
      nextFilters.type = "all";
      nextFilters.status = "all";
      nextFilters.reviewStatus = "all";
      nextFilters.author = "all";
      nextFilters.sort = "date";
    }

    this.filters = nextFilters;
    this.showBetaType = nextFilters.type === "all" ? undefined : nextFilters.type;
    this.showBetaStatus = nextFilters.status === "all" ? undefined : nextFilters.status;
    this.showBetaReviewStatus = nextFilters.reviewStatus === "all" ? undefined : nextFilters.reviewStatus;
    this.showBetaAuthor = nextFilters.author === "all" ? undefined : nextFilters.author;
    this.betaSortBy = nextFilters.sort === "date" ? undefined : nextFilters.sort;
    this.findJiraTicketNumber = nextFilters.jiraTicketNumber || undefined;

    return this.filters;
  }

  resetFilters() {
    const defaults: BetaFilters = {
      type: "all",
      status: "all",
      reviewStatus: "all",
      author: "all",
      sort: "date",
      jiraTicketNumber: "",
    };
    return this.applyFilters(defaults, false);
  }

  getQueryParams() {
    const queryParams = {
      type: this.showBetaType,
      status: this.showBetaStatus,
      reviewStatus: this.showBetaReviewStatus,
      author: this.showBetaAuthor,
    };

    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    return queryParams;
  }

  get betaFilterSettings() {
    if (!this._betaFilterSettings) {
      this._betaFilterSettings = this.storage.getItem(StorageKeys.BETA_FILTER_SETTINGS) || {};
    }
    return this._betaFilterSettings;
  }

  set betaFilterSettings(value: any) {
    if (value) {
      this._betaFilterSettings = value;
      this.storage.setItem(StorageKeys.BETA_FILTER_SETTINGS, value);
    } else {
      this._betaFilterSettings = undefined;
      this.storage.removeItem(StorageKeys.BETA_FILTER_SETTINGS);
    }
  }

  getViewMode(defaultValue = "list") {
    return this.betaFilterSettings.viewMode || defaultValue;
  }

  setViewMode(view: string) {
    this.betaFilterSettings = {
      ...this.betaFilterSettings,
      viewMode: view,
    };
  }

  getAnnouncementsCollapsed(defaultValue = false) {
    return this.betaFilterSettings.announcementsCollapsed !== undefined ? this.betaFilterSettings.announcementsCollapsed : defaultValue;
  }

  setAnnouncementsCollapsed(collapsed: boolean) {
    this.betaFilterSettings = {
      ...this.betaFilterSettings,
      announcementsCollapsed: collapsed,
    };
  }

  fetchBetaAnnouncements(betaStringId: string, collectionRef?: any) {
    const collection = collectionRef;
    if (!collection || !betaStringId) return Promise.resolve([]);
    return collection.ref
      .where("parentID", "==", null)
      .where("beta_string_id", "==", betaStringId)
      .where("pinned", "==", true)
      .get()
      .then((result) => {
        let pinnedPosts = [];
        result.forEach((doc) => {
          let post: any = doc.data();
          if (!post.deleted) {
            post.id = doc.id;
            pinnedPosts.push(post);
          }
        });
        return pinnedPosts;
      });
  }
}
